import { useCallback, useEffect, useRef, useState } from 'react'

import { Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson, F0Checkbox, F0Link } from '@factorialco/f0-react'
import {
  RichTextEditor,
  RichTextEditorHandle,
  resultType,
} from '@factorialco/f0-react/dist/experimental'
import { Calendar, FileFilled } from '@factorialco/f0-react/icons/app'
import { PostsGroup } from 'generated/resources/datatable.graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import TimePicker from 'modules/core/components/TimePicker'
import { Access, Employee } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SchedulePostModal from 'modules/posts/components/SchedulePost/SchedulePostModal'
import { useCreatePost } from 'modules/posts/hooks/useCreatePost'

interface CommunityQuickPostEditorProps {
  groupId?: string
  communities: Array<{
    id: PostsGroup['id']
    title: PostsGroup['title']
  }>
  currentUser: {
    employeeId: Employee['id']
    firstName: Access['firstName']
    lastName: Access['lastName']
    fullName?: Access['fullName']
    avatar?: Access['avatar']
  }
}

type PostForm = {
  allowCommentsAndReactions: boolean
  attachments?: Array<File>
  coverImage?: Array<File>
  description: string
  location?: string
  mentionedAccessIds: Array<number>
  postGroupId: number
  publishedAt: string | null
  sendNotifications: boolean
  startsAt?: string
  title: string
  type: 'announcement' | 'event' | 'kudo'
}

function CommunityQuickPostEditor({
  groupId,
  communities = [],
  currentUser,
}: CommunityQuickPostEditorProps) {
  const { editorConfig, loading: loadingEditorConfig } = useRichTextEditor()
  const editorRef = useRef<RichTextEditorHandle>(null)

  const [focused, setFocused] = useState(false)
  const [description, setDescription] = useState('')
  const [eventDay, setEventDay] = useState(moment().startOf('day').format('L'))
  const [eventTime, setEventTime] = useState('00:00')

  const [showScheduleModal, setShowScheduleModal] = useState(false)

  const { createPost, loadingCreatePost } = useCreatePost()

  async function submitPost(data: PostForm) {
    const result = await createPost(data)
    const handledResult = handleResult(result, () => {})

    if (handledResult.success) {
      clearEditor()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.quick_post.error_message'),
      })
    }

    return handledResult
  }

  const postForm = useForm<PostForm>({
    defaultValues: {
      allowCommentsAndReactions: true,
      attachments: undefined,
      coverImage: undefined,
      description: description,
      location: undefined,
      mentionedAccessIds: [],
      postGroupId: Number(groupId),
      publishedAt: moment().toISOString(),
      sendNotifications: true,
      startsAt: undefined,
      title: '',
      type: 'announcement',
    },
    onSubmit: async (data) => submitPost(data),
  })

  async function savePost(postDate?: string | null) {
    const publishedAt = postDate === undefined ? moment().toISOString() : postDate
    const nextValues = { ...postForm.values, publishedAt }
    await postForm.setValues(nextValues)
    await submitPost(nextValues)
  }

  function handleSchedule(scheduledDate: string) {
    savePost(scheduledDate)
    setShowScheduleModal(false)
  }

  function handleSaveDraft() {
    savePost(null)
  }

  const handleCollapsedFocus = useCallback(() => {
    setFocused(true)
  }, [])

  useEffect(() => {
    if (focused) {
      editorRef.current?.focus()
    }
  }, [focused])

  useEffect(() => {
    if (groupId) {
      postForm.setValues({ ...postForm.values, postGroupId: Number(groupId) })
    }
    setFocused(false)
  }, [groupId])

  function getCommunityNameById(id: number): string {
    const community = communities.find((c) => c.id === id)
    return community ? community.title : ''
  }

  function onChangeCommunity(nextCommunityId: string) {
    const selectedCommunity = communities.find((c) => c.id.toString() === nextCommunityId)
    if (selectedCommunity) {
      postForm.setValues({ ...postForm.values, postGroupId: selectedCommunity.id })
    }
  }

  function onChangePostContent(result: resultType) {
    const description = result.value ?? ''
    setDescription(description)
    const title = getTitleFromContent(description)
    postForm.setValues({ ...postForm.values, title, description })
  }

  function getTitleFromContent(content: string): string {
    if (!content.trim()) return i18n.t('posts.communities.quick_post.default_title')

    let contentToUse = content

    const firstBreakIndex =
      ['<br>', '</p>']
        .map((pattern) => content.indexOf(pattern))
        .filter((index) => index !== -1)
        .sort((a, b) => a - b)[0] ?? -1

    if (firstBreakIndex !== -1) {
      contentToUse = content.substring(0, firstBreakIndex)
    }

    const plainText = removeHTMLTags(contentToUse)
    const maxLength = 60
    if (plainText.length <= maxLength) return plainText

    const truncated = plainText.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(' ')
    const contentFirstWords =
      lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated
    return `${contentFirstWords}...`
  }

  function removeHTMLTags(htmlString: string): string {
    return htmlString.replace(/<[^>]*>/g, '').trim()
  }

  function onChangeIsEvent(nextIsEvent: boolean) {
    const type = nextIsEvent ? 'event' : 'announcement'
    postForm.setValues({ ...postForm.values, type })
  }

  function onChangeDay(day: string) {
    const localeData = moment.localeData()
    const format = localeData.longDateFormat('L')
    const newDate = moment(day, format)
    const time = moment(eventTime, 'HH:mm')
    const combinedDate = newDate
      .clone()
      .set('hour', time.hour())
      .set('minute', time.minute())
      .set('second', 0)
      .set('millisecond', 0)
    setEventDay(day)
    postForm.setValues({ ...postForm.values, startsAt: combinedDate.format() })
  }

  function onChangeTime(time: string) {
    const newTime = moment(time, 'HH:mm')
    const localeData = moment.localeData()
    const format = localeData.longDateFormat('L')
    const currentDate = moment(eventDay, format)
    const combinedDate = currentDate
      .clone()
      .set('hour', newTime.hour())
      .set('minute', newTime.minute())
      .set('second', 0)
      .set('millisecond', 0)
    setEventTime(newTime.format('HH:mm'))
    postForm.setValues({ ...postForm.values, startsAt: combinedDate.format() })
  }

  function onChangeAttachments(attachments: Array<File>) {
    postForm.setValues({ ...postForm.values, attachments })
  }

  function clearEditor() {
    postForm.resetForm()
    setFocused(false)
  }

  const isEvent = postForm.values.type === 'event'
  const isPostDisabled = loadingCreatePost || !postForm.values.description.trim()

  if (loadingEditorConfig) {
    return <RichTextEditor.Skeleton rows={1} />
  }

  if (!focused) {
    return (
      <QuickPostEditorCollapsed
        communityName={getCommunityNameById(postForm.values.postGroupId)}
        currentUser={currentUser}
        handleFocus={handleCollapsedFocus}
      />
    )
  }

  return (
    <Box gap="s16">
      {currentUser && (
        <Box
          flexDirection="row"
          alignItems="center"
          gap="s12"
          role={'region'}
          aria-label={i18n.t('posts.communities.quick_post.header_label')}
        >
          <F0Link
            href={`/employees/${currentUser.employeeId}`}
            title={currentUser.firstName}
            className="font-medium text-f1-foreground no-underline visited:text-f1-foreground"
            stopPropagation
          >
            <F0AvatarPerson
              firstName={currentUser.firstName}
              lastName={currentUser.lastName}
              src={buildUrl(currentUser.avatar)}
            />
          </F0Link>
          <Box flexDirection="row" alignItems="center" gap="s8" style={{ width: '100%' }}>
            <F0Link
              href={`/employees/${currentUser.employeeId}`}
              title={currentUser.fullName}
              className="font-medium text-f1-foreground no-underline visited:text-f1-foreground"
              style={{ flexShrink: 0 }}
              stopPropagation
            >
              {`${currentUser.fullName}`}
            </F0Link>
            <Text.Normal size="200" color="grey800">
              {i18n.t('common.in')}
            </Text.Normal>
            <Box style={{ flexShrink: '0' }}>
              <Select
                aria-label={i18n.t('settings.locations.location.work_spaces.add.type')}
                options={communities.map((community) => ({
                  label: community.title,
                  value: community.id.toString(),
                }))}
                value={postForm.values.postGroupId.toString()}
                readonly={groupId !== undefined}
                onChange={onChangeCommunity}
              />
            </Box>
          </Box>
        </Box>
      )}
      <RichTextEditor
        ref={editorRef}
        {...editorConfig}
        title={getTitleFromContent(postForm.values.description)}
        placeholder={i18n.t('posts.communities.quick_post.placeholder')}
        maxCharacters={10000}
        initialEditorState={{ content: postForm.values.description }}
        onChange={onChangePostContent}
        filesConfig={{
          acceptedFileType: ['image', 'video', 'pdf'],
          multipleFiles: true,
          onFiles: onChangeAttachments,
        }}
        primaryAction={{
          action: {
            label: loadingCreatePost
              ? i18n.t('common.saving')
              : i18n.t('posts.communities.quick_post.button'),
            onClick: savePost,
            variant: 'default',
            disabled: isPostDisabled,
          },
          ...(!isPostDisabled && {
            subActions: [
              {
                label: i18n.t('posts.communities.schedule_post.title'),
                onClick: () => setShowScheduleModal(true),
                icon: Calendar,
              },
              {
                label: i18n.t('posts.communities.quick_post.save_as_draft'),
                onClick: handleSaveDraft,
                icon: FileFilled,
              },
            ],
          }),
        }}
      />
      <Box
        gap={'s12'}
        flexGrow
        paddingX="s16"
        paddingY="s16"
        style={{ border: '1px solid #e0e0e0', borderRadius: '12px' }}
      >
        <TogglerCheckbox
          value={isEvent}
          onChange={onChangeIsEvent}
          label={i18n.t('posts.communities.quick_post.is_event_label')}
        />
        {isEvent && (
          <Box flexDirection="row" gap="s12">
            <Box gap={'s6'} style={{ width: '30%' }}>
              <DayPicker
                value={eventDay}
                onChange={onChangeDay}
                minDate={moment().startOf('day')}
                mandatory={isEvent}
                label={i18n.t('posts.communities.quick_post.day_picker_label')}
              />
            </Box>
            <Box gap={'s6'} style={{ width: '20%' }}>
              <TimePicker
                value={eventTime}
                onChange={onChangeTime}
                attachmentPosition="bottom right"
                mandatory={isEvent}
                label={i18n.t('posts.communities.quick_post.time_picker_label')}
              />
            </Box>
            <Box gap={'s6'} style={{ width: '50%' }}>
              <TextField
                value={postForm.values.location || ''}
                placeholder={i18n.t('posts.communities.quick_post.event_location_placeholder')}
                onChange={(location) => postForm.setValues({ ...postForm.values, location })}
                label={i18n.t('posts.communities.quick_post.location_label')}
              />
            </Box>
          </Box>
        )}
      </Box>
      <F0Checkbox
        title={i18n.t('posts.communities.quick_post.turn_off_interactions')}
        checked={!postForm.values.allowCommentsAndReactions}
        onCheckedChange={(checked: boolean) => {
          postForm.setValues({ ...postForm.values, allowCommentsAndReactions: !checked })
        }}
      />
      <F0Checkbox
        title={i18n.t('posts.communities.quick_post.do_not_send_notification')}
        checked={!postForm.values.sendNotifications}
        onCheckedChange={(checked: boolean) => {
          postForm.setValues({ ...postForm.values, sendNotifications: !checked })
        }}
      />
      <SchedulePostModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onConfirm={handleSchedule}
      />
    </Box>
  )
}

function QuickPostEditorCollapsed({
  communityName,
  currentUser,
  handleFocus,
}: {
  communityName: string
  currentUser: CommunityQuickPostEditorProps['currentUser']
  handleFocus: () => void
}) {
  return (
    <Box gap="s12" paddingY="s16" flexDirection="row" alignItems="center">
      <F0AvatarPerson
        firstName={currentUser.firstName}
        lastName={currentUser.lastName}
        src={buildUrl(currentUser.avatar)}
      />
      <TextField
        onFocus={handleFocus}
        placeholder={i18n.t('posts.communities.quick_post.collapsed_placeholder', {
          communityName,
        })}
        label={i18n.t('posts.communities.quick_post.collapsed_label')}
        hideLabel
      />
    </Box>
  )
}

export default CommunityQuickPostEditor
